import React, { useState, useEffect } from "react";
import {
  Link as RouterLink,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import Toaster from "../utils/Toaster";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, IconButton, InputAdornment } from "@mui/material";
import AppButton from "../common/Button";
import { Container } from "../common/Container";
import { AppTypography } from "../common/Typography";
import Card from "../common/Card";
import { PasswordInput } from "../common/Input";
import { useTheme } from "../../theme/ThemeContext";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";

const ResetPassword = () => {
  const theme = useTheme();
  const location = useLocation();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email] = useState("suhail@gmail.com");
  const navigate = usePreserveStoreIdNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // const emailParam = searchParams.get("email");
    // if (emailParam) {
    //   setEmail(decodeURIComponent(emailParam));
    // } else {
    //   Toaster.show("Invalid reset link. Please try again.", "error");
    //   navigate("/forgot-password", { replace: true });
    // }
  }, [searchParams, navigate]);

  const handleChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (!formData.password || !formData.confirmPassword) {
      Toaster.show("Please fill in all fields", "warning");
      return false;
    }

    if (formData.password.length < 6) {
      Toaster.show("Password must be at least 6 characters long", "warning");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      Toaster.show("Passwords do not match", "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate API call for password reset
      await new Promise((resolve) => setTimeout(resolve, 2000));

      Toaster.show(
        "Password reset successful! You can now login with your new password.",
        "success"
      );

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 2000);
    } catch (error) {
      Toaster.show("Password reset failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return null; // Don't render until email is loaded
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...theme?.background,
        flex: 1,
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Logo/Brand Section */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
            }}
          >
            <AppTypography
              variant="h3"
              style={theme?.typography?.heading}
              fontWeight={700}
              fontSize="2.5rem"
              textAlign="center"
              mb={1}
            >
              Reset Password
            </AppTypography>
            <AppTypography
              variant="body1"
              style={theme?.typography?.body}
              fontSize="1.1rem"
              textAlign="center"
              sx={{ opacity: 0.9 }}
            >
              Enter your new password for {email}
            </AppTypography>
          </Box>

          {/* Reset Password Card */}
          <Card
            variant="elevated"
            sx={{
              width: "100%",
              maxWidth: 400,
              p: 4,
              borderRadius: 2,
              ...theme?.layout_styles?.paper,
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              autoComplete="on"
              noValidate
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              {/* New Password Input */}
              <Box sx={{ position: "relative" }}>
                <PasswordInput
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                  size="large"
                  label="New Password"
                  placeholder="Enter your new password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                          size="small"
                          style={theme?.typography?.body}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Confirm Password Input */}
              <Box sx={{ position: "relative" }}>
                <PasswordInput
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                  size="large"
                  label="Confirm New Password"
                  placeholder="Confirm your new password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword((s) => !s)}
                          edge="end"
                          size="small"
                          style={theme?.typography?.body}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Submit Button */}
              <AppButton
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  ...theme?.button_styles?.app,
                }}
              >
                {loading ? "Resetting Password..." : "Reset Password"}
              </AppButton>

              {/* Divider */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    ...theme?.divider,
                  }}
                />
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  or
                </AppTypography>
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    ...theme?.divider,
                  }}
                />
              </Box>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: "center" }}>
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  Remember your password?{" "}
                  <AppTypography
                    component={RouterLink}
                    to={`/login${location.search || ""}`}
                    style={theme?.typography?.link}
                    fontSize="0.875rem"
                    fontWeight={600}
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Sign in here
                  </AppTypography>
                </AppTypography>
              </Box>

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: "center" }}>
                <AppTypography
                  variant="body2"
                  style={theme?.typography?.body}
                  fontSize="0.875rem"
                  textAlign="center"
                  sx={{ opacity: 0.7 }}
                >
                  Need to send another reset link?{" "}
                  <AppTypography
                    component={RouterLink}
                    to={`/forgot-password${location.search || ""}`}
                    style={theme?.typography?.link}
                    fontSize="0.875rem"
                    fontWeight={600}
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Click here
                  </AppTypography>
                </AppTypography>
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword;
